import type { HqApi, ScreenDeviceInfo, ScreenDeviceTargets } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Input,
  Result,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message
} from 'antd';
import type { InputRef } from 'antd';
import type { ClipboardEvent, CSSProperties, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  parseScreenCode,
  sanitizeScreenCodeInput,
  SCREEN_CODE_LENGTH,
  SCREEN_CODE_SEGMENT_LENGTH,
  SCREEN_CODE_SEGMENTS
} from './code';

export interface ScreenScanResult {
  requestCode: string;
  deviceName?: string | null;
  tenantName?: string | null;
  orgName?: string | null;
}

export interface ScreenScanConfirmProps {
  /**
   * 已登录会话的 SDK 实例（各端各自注入，复用其 baseUrl 与令牌）。
   * 用登录态确认：不需要二次登录，填完申请码直接确认。
   */
  hq: HqApi;
  /** 确认成功回调（如刷新「大屏设备」列表、关闭弹窗）。 */
  onApproved?: (result: ScreenScanResult) => void;
}

/** 空段占位：与屏上 4-4-4 分组一一对应。 */
const EMPTY_SEGMENTS = Array.from({ length: SCREEN_CODE_SEGMENTS }, () => '');

/** 输入框字号/宽度：够看清 4 位、又不至于在页面上显得笨重。 */
const SEGMENT_INPUT_STYLE: CSSProperties = {
  width: 76,
  height: 44,
  paddingInline: 4,
  textAlign: 'center',
  fontSize: 20,
  letterSpacing: 3,
  fontFamily: 'Consolas, "Courier New", monospace'
};

/**
 * 确认大屏（PC 端）：输入大屏上显示的 12 位申请码（4-4-4 三段）→ 预览申请 → 选择机构 → 确认。
 *
 * 只保留手工输入：浏览器摄像头（getUserMedia）在 iOS/安卓上普遍没有权限、且大屏多在内网 HTTP 环境
 * （非安全上下文直接不可用），扫码通道已下线，避免给出一个点了必然失败的按钮。
 *
 * 授权范围由登录身份决定，前端不做二次判断，只按后端 targets 渲染：
 * · SYSTEM / OPS（有 sys:config:manage）→ 可搜索筛选租户与机构，决定这块屏显示谁的数据；
 * · GOV / HOSPITAL 管理人员 → 固定为自己所属机构，只读展示。
 *
 * 业务可扩展：本组件只消费申请的 bizType/bizName，新增业务类型无需改动。
 */
export function ScreenScanConfirm({ hq, onApproved }: ScreenScanConfirmProps) {
  const [segments, setSegments] = useState<string[]>(EMPTY_SEGMENTS);
  const [code, setCode] = useState<string | null>(null);
  const [info, setInfo] = useState<ScreenDeviceInfo | null>(null);
  const [targets, setTargets] = useState<ScreenDeviceTargets | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [orgId, setOrgId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ScreenScanResult | null>(null);
  const segmentRefs = useRef<Array<InputRef | null>>([]);

  const filled = segments.join('');
  const complete = filled.length === SCREEN_CODE_LENGTH;

  const focusSegment = useCallback((index: number) => {
    const target = segmentRefs.current[Math.min(Math.max(index, 0), SCREEN_CODE_SEGMENTS - 1)];
    target?.focus();
    target?.select();
  }, []);

  // 单段输入：即时过滤（大写化 + 折叠 O/I/L + 丢弃连字符），满 4 位自动跳下一段
  const onSegmentChange = (index: number, raw: string) => {
    const clean = sanitizeScreenCodeInput(raw).slice(0, SCREEN_CODE_SEGMENT_LENGTH);
    setSegments((prev) => {
      const next = [...prev];
      next[index] = clean;
      return next;
    });
    setError(null);
    if (clean.length >= SCREEN_CODE_SEGMENT_LENGTH && index < SCREEN_CODE_SEGMENTS - 1) focusSegment(index + 1);
  };

  const submit = () => {
    if (!complete) {
      setError(`请填写完整的 ${SCREEN_CODE_LENGTH} 位申请码（每段 ${SCREEN_CODE_SEGMENT_LENGTH} 位）`);
      return;
    }
    const parsed = parseScreenCode(filled);
    if (!parsed) {
      setError('申请码格式不正确，请对照大屏重新填写');
      return;
    }
    setError(null);
    setCode(parsed);
  };

  // 粘贴 / 扫码枪整码：从当前段起按 4 位切分铺满后续段
  const onSegmentPaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    const clean = sanitizeScreenCodeInput(text);
    if (!clean) return;
    event.preventDefault();
    setSegments((prev) => {
      const next = [...prev];
      let cursor = index;
      let offset = 0;
      while (cursor < SCREEN_CODE_SEGMENTS && offset < clean.length) {
        next[cursor] = clean.slice(offset, offset + SCREEN_CODE_SEGMENT_LENGTH);
        offset += SCREEN_CODE_SEGMENT_LENGTH;
        cursor += 1;
      }
      return next;
    });
    setError(null);
    focusSegment(Math.min(index + Math.floor(clean.length / SCREEN_CODE_SEGMENT_LENGTH), SCREEN_CODE_SEGMENTS - 1));
  };

  const onSegmentKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !segments[index] && index > 0) {
      event.preventDefault();
      setSegments((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      focusSegment(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusSegment(index - 1);
      return;
    }
    if (event.key === 'ArrowRight' && index < SCREEN_CODE_SEGMENTS - 1) {
      event.preventDefault();
      focusSegment(index + 1);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  // 1) 读取申请信息（匿名端点，码本身即凭据）
  useEffect(() => {
    if (!code) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    setInfo(null);
    setTargets(null);
    void hq.auth.screenDevice
      .inspect(code)
      .then((data) => {
        if (!alive) return;
        setInfo(data);
        setDeviceName(data.deviceName ?? '');
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : '申请不存在或已失效，请在大屏上刷新申请码后重试');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [code, hq]);

  // 2) 我能把这块屏授权给谁（GOV/HOSPITAL 返回固定机构；SYSTEM/OPS 返回可选清单）
  useEffect(() => {
    if (!code) return undefined;
    let alive = true;
    void hq.tenant
      .screenDeviceTargets(tenantId || undefined)
      .then((data) => {
        if (!alive) return;
        setTargets(data);
        setTenantId(data.tenantId);
        setOrgId(data.orgId);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : '当前账号没有大屏授权权限');
      });
    return () => {
      alive = false;
    };
  }, [code, hq, tenantId]);

  const onApprove = async () => {
    if (!code || !targets) return;
    if (targets.canChooseTenant && !tenantId) {
      message.error('请选择这块大屏要显示哪个租户的数据');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await hq.tenant.approveScreenDevice(code, {
        tenantId: targets.canChooseTenant ? tenantId : undefined,
        orgId: targets.canChooseOrg ? orgId || '0' : undefined,
        deviceName: deviceName.trim() || undefined
      });
      const payload: ScreenScanResult = {
        requestCode: code,
        deviceName: result.deviceName ?? deviceName,
        tenantName: result.tenantName,
        orgName: result.orgName
      };
      setDone(payload);
      onApproved?.(payload);
      message.success('已确认授权，大屏将在几秒内自动进入');
    } catch (err) {
      setError(err instanceof Error ? err.message : '确认失败');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSegments(EMPTY_SEGMENTS);
    setCode(null);
    setInfo(null);
    setTargets(null);
    setTenantId('');
    setOrgId('');
    setDeviceName('');
    setDone(null);
    setError(null);
  };

  const scopeText = useMemo(() => {
    if (!targets) return '';
    const tenantName = targets.tenantName ?? targets.tenants.find((item) => item.id === tenantId)?.name ?? '';
    const orgName =
      targets.orgName ??
      (orgId === '0' || !orgId ? '全区（监管视角）' : targets.orgs.find((item) => item.id === orgId)?.name ?? '');
    return `${tenantName} / ${orgName}`;
  }, [orgId, targets, tenantId]);

  /** 三段输入区域：固定宽度居中对齐，避免宽屏下拉成一条「大输入框」 */
  const codeInput = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'nowrap' }}>
      {segments.map((value, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {index > 0 ? <span style={{ color: '#c9c9c9', fontSize: 16 }}>-</span> : null}
          <Input
            ref={(node) => {
              segmentRefs.current[index] = node;
            }}
            value={value}
            onChange={(event) => onSegmentChange(index, event.target.value)}
            onPaste={(event) => onSegmentPaste(index, event)}
            onKeyDown={(event) => onSegmentKeyDown(index, event)}
            onFocus={(event) => event.target.select()}
            maxLength={SCREEN_CODE_SEGMENT_LENGTH}
            autoFocus={index === 0}
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            aria-label={`申请码第 ${index + 1} 段`}
            style={SEGMENT_INPUT_STYLE}
          />
        </div>
      ))}
    </div>
  );

  return (
    <Card title="申请码确认">
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      {done ? (
        <Result
          status="success"
          title="已确认授权"
          subTitle={`${done.deviceName ?? '这块大屏'} 将显示：${[done.tenantName, done.orgName ?? '全区（监管视角）']
            .filter(Boolean)
            .join(' / ')}`}
          extra={
            <Space>
              <Button type="primary" onClick={reset}>
                继续确认下一块
              </Button>
            </Space>
          }
        />
      ) : code ? (
        loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : info ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="业务">{info.bizName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="大屏">{info.deviceName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="来源 IP">{info.requestIp ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="申请时间">
                {info.createAt ? formatDisplayTime(info.createAt) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                {info.status === 'PENDING' ? (
                  <Tag color="gold">待确认</Tag>
                ) : info.status === 'AUTHORIZED' ? (
                  <Tag color="blue">已确认，等大屏领取</Tag>
                ) : (
                  <Tag>{info.status}</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              请先核对「来源 IP」与现场大屏一致，再确认授权。授权后大屏会自动进入，无需在屏上操作。
            </Typography.Paragraph>

            <Divider style={{ margin: 0 }} />

            {targets?.canChooseTenant ? (
              <div>
                <Typography.Text>显示哪个租户的数据</Typography.Text>
                <Select
                  style={{ width: '100%', marginTop: 6 }}
                  value={tenantId || undefined}
                  onChange={(value) => {
                    setTenantId(value);
                    setOrgId('');
                  }}
                  showSearch
                  optionFilterProp="label"
                  placeholder="搜索并选择租户"
                  options={targets.tenants.map((item) => ({
                    value: item.id,
                    label: `${item.name}（${item.code}）`
                  }))}
                />
              </div>
            ) : null}

            {targets?.canChooseOrg ? (
              <div>
                <Typography.Text>显示哪个机构的数据</Typography.Text>
                <Select
                  style={{ width: '100%', marginTop: 6 }}
                  value={orgId || '0'}
                  onChange={setOrgId}
                  showSearch
                  optionFilterProp="label"
                  placeholder="搜索并选择机构（不选=全区）"
                  options={[
                    { value: '0', label: '全区（监管视角）' },
                    ...targets.orgs.map((item) => ({ value: item.id, label: `${item.name}（${item.code}）` }))
                  ]}
                />
              </div>
            ) : targets ? (
              <Alert type="info" showIcon message={`将授权为：${scopeText}`} />
            ) : null}

            <div>
              <Typography.Text>大屏名称（可选）</Typography.Text>
              <Input
                style={{ marginTop: 6 }}
                value={deviceName}
                onChange={(event) => setDeviceName(event.target.value)}
                maxLength={64}
                placeholder="例如：门诊一楼大屏"
              />
            </div>

            <Space>
              <Button type="primary" size="large" loading={busy} disabled={!targets} onClick={() => void onApprove()}>
                确认授权这块大屏
              </Button>
              <Button size="large" onClick={reset}>
                取消
              </Button>
            </Space>
          </Space>
        ) : null
      ) : (
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
            message="请在大屏上找到申请码"
            description={`屏上显示为 ${SCREEN_CODE_SEGMENTS} 段，共 ${SCREEN_CODE_LENGTH} 位；按顺序填入下方输入框，不区分大小写，连字符可省略。`}
          />

          <div style={{ padding: '4px 0 8px' }}>{codeInput}</div>

          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Space size={12}>
              <Button type="primary" disabled={!complete} onClick={submit}>
                读取申请
              </Button>
              <Button
                onClick={() => {
                  setSegments(EMPTY_SEGMENTS);
                  setError(null);
                  focusSegment(0);
                }}
              >
                清空
              </Button>
            </Space>
          </div>

          <Typography.Paragraph type="secondary" style={{ marginTop: 20, marginBottom: 0, fontSize: 12 }}>
            申请码有效期为 10 分钟；过期后大屏会自动换码，重新填写即可。
          </Typography.Paragraph>
        </div>
      )}
    </Card>
  );
}
